import { useEffect, useRef, useState } from "react";
import { api, type AssistantModel, type AssistantPreview, type AssistantSpend } from "../api.ts";
import { C, mono, sans } from "../theme.ts";
import { Markdown } from "./Markdown.tsx";
import { useCloseOnBack } from "../useCloseOnBack.ts";

// The assistant. Opened from "Ask AI" in the bottom nav's menu, over
// whatever tab is open — it reads the whole board, not just one screen.
//
// ───────────────────────────────────────────────────────────────────────────
// Read this before changing anything here.
//
// The rule the assistant runs under is stated in the system prompt and made
// structural in packages/api/src/assistant.ts, which is where the guard
// belongs. What this file must not do is undo it at the edge: no summary line
// about how the conversation is going, no count of messages, no "you've asked
// about this before", and no badge on the button standing for anything unread.
//
// The button says one thing and the sheet shows what was said. That is all.
//
// ── it can change the board now, and this is where that is shown ──────────
//
// A turn that wants to change something comes back having changed nothing,
// carrying a card: every field, before and after. The board is untouched
// while that card is on screen. Confirm runs it; Cancel does not; and what
// ran can be put back.
//
// Two things about this that are load-bearing rather than styling:
//
//   · **The card is not a summary of what happened.** It is what would happen.
//     If a later change makes it render after the fact, the guarantee is gone
//     and only the wording will still claim it.
//   · **Undo is a button, not a sentence.** Asking the assistant to undo
//     something costs a turn, may not work, and depends on it understanding
//     what "that" was. The button calls one endpoint that runs the recorded
//     inverse. It makes no model call, so it costs nothing and works when the
//     key does not.
// ───────────────────────────────────────────────────────────────────────────

const KEY = "mc.assistant";

/**
 * What is on screen. The wire transcript below it is what the model sees.
 *
 * A `card` is a change that was proposed. `state` is what became of it, and it
 * is kept after the fact so the conversation reads back as it happened — the
 * change he was shown, and what he said to it.
 */
type Turn =
  | { who: "you"; text: string }
  | { who: "it"; text: string }
  | {
      who: "card";
      previews: AssistantPreview[];
      state: "open" | "done" | "cancelled";
      /** The undo handle, once it has been applied and if anything is reversible. */
      batch?: string;
      undone?: boolean;
    };

interface Saved { turns: Turn[]; wire: unknown[] }

function read(): Saved {
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) ?? "null");
    if (!raw || !Array.isArray(raw.turns)) return { turns: [], wire: [] };
    return { turns: raw.turns, wire: raw.wire ?? [] };
  } catch {
    return { turns: [], wire: [] };
  }
}

// sessionStorage rather than localStorage: a conversation belongs to the sitting
// it happened in. It survives a tab reload and is gone when the app is closed,
// which is the right lifetime for something with no history anywhere else.
function save(s: Saved) {
  try { sessionStorage.setItem(KEY, JSON.stringify(s)); } catch { /* no storage, no memory */ }
}

/** Rounded to the cent it will actually appear on a statement as. */
const money = (usd: number) => `$${usd < 0.01 && usd > 0 ? usd.toFixed(4) : usd.toFixed(2)}`;

/** `next` reads as a label, not as a column name, when it is on a card. */
const FIELD_LABELS: Record<string, string> = {
  next: "next action", why: "why", follow_up_on: "chase on", blocked_until: "not before",
  needs_business_hours: "needs an open office", parked_because: "because", review_when: "look again",
  mins: "minutes", area_id: "area", value_id: "value",
};
const label = (f: string) => FIELD_LABELS[f] ?? f.replace(/_/g, " ");

/**
 * What would happen, field by field. Rendered before anything runs.
 *
 * The arrow is the whole design: what it says now on the left, what it would
 * say on the right. A change he cannot read in one glance is a change he
 * cannot meaningfully agree to, which would make the confirmation a formality
 * rather than a guard.
 */
function Card({ previews }: { previews: AssistantPreview[] }) {
  return (
    <>
      {previews.map((p, i) => (
        <div key={i} style={{
          background: C.card, border: `1px solid ${C.ruleStrong}`, borderRadius: 12,
          padding: "11px 13px", marginBottom: 8,
        }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.4, color: C.ink, marginBottom: 7 }}>
            {p.headline}
          </div>
          {p.lines.map((l, j) => (
            <div key={j} style={{
              fontFamily: mono, fontSize: 11.5, lineHeight: 1.6, color: C.inkSoft,
              display: "flex", gap: 6, flexWrap: "wrap",
            }}>
              <span style={{ color: C.inkGhost, minWidth: 88 }}>{label(l.field)}</span>
              {l.from != null && <><span style={{ textDecoration: "line-through", color: C.inkFaint }}>{l.from}</span><span style={{ color: C.inkGhost }}>→</span></>}
              <span style={{ color: C.ink, flex: 1 }}>{l.to ?? "—"}</span>
            </div>
          ))}
          {/* A caution is never a warning about him. It is about the change:
              this one cannot be taken back, this one has no why yet. */}
          {p.caution && (
            <div style={{
              fontSize: 12, lineHeight: 1.45, marginTop: 8, paddingTop: 7,
              borderTop: `1px solid ${C.rule}`,
              color: p.undoable ? C.inkFaint : C.alert,
            }}>{p.caution}</div>
          )}
        </div>
      ))}
    </>
  );
}

export function Assistant({ onClose, seed }: { onClose: () => void; seed?: string }) {
  // Mounted only while the assistant is open — see the file for why an
  // Android back press should close this rather than exit the app.
  useCloseOnBack(onClose);

  const [turns, setTurns] = useState<Turn[]>(() => read().turns);
  const [wire, setWire] = useState<unknown[]>(() => read().wire);
  // `seed` (from a task's "Dive in") only ever pre-fills the draft, never
  // auto-sends — and only into a fresh conversation, so it can't overwrite
  // something already being written into an ongoing one.
  const [draft, setDraft] = useState(() => (seed && read().turns.length === 0 ? seed : ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<AssistantModel[]>([]);
  const [model, setModel] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [lastSpend, setLastSpend] = useState<AssistantSpend | null>(null);
  const foot = useRef<HTMLDivElement | null>(null);

  // The open proposal, held here and never written to sessionStorage.
  //
  // The server keeps proposals in memory, so one does not survive a restart
  // and expires in fifteen minutes — a card restored from storage would be a
  // Confirm button that cannot work. The wire is not saved while a card is
  // open either: it ends mid-turn, with a request for a change and no answer
  // to it, and sending that back is not a conversation the API will accept.
  const [pending, setPending] = useState<{ id: string; wire: unknown[] } | null>(null);

  useEffect(() => {
    if (models.length) return;
    Promise.all([api.assistantOptions(), api.settings()])
      .then(([opts, s]) => { setModels(opts.models); setModel(s.model ?? opts.default_model); })
      .catch(() => { /* the picker stays closed; the server still has a default */ });
  }, [models.length]);

  useEffect(() => { foot.current?.scrollIntoView({ block: "end" }); }, [turns, busy]);

  /**
   * What every turn ends with, whether it was a question, a yes or a no.
   *
   * A turn carrying `pending` is the one case where nothing is saved: the card
   * lives in React until it is answered. Everything else lands in the
   * transcript and in storage together.
   */
  const settle = (
    shown: Turn[],
    r: Awaited<ReturnType<typeof api.assistantChat>>,
    appliedTo?: number,
  ) => {
    let next = [...shown];
    if (r.reply) next.push({ who: "it", text: r.reply });

    // Mark the card he just answered with what became of it, and hang the undo
    // handle on it. `undoable` is false for a batch that was only a log line.
    if (appliedTo != null && r.applied) {
      const card = next[appliedTo];
      if (card?.who === "card") {
        next[appliedTo] = {
          ...card, state: "done",
          ...(r.applied.undoable ? { batch: r.applied.id } : {}),
        };
      }
    }

    setLastSpend(r.spend);
    if (r.pending) {
      next = [...next, { who: "card", previews: r.pending.previews, state: "open" }];
      setTurns(next);
      setPending({ id: r.pending.proposal_id, wire: r.messages });
      return;
    }
    setPending(null);
    setTurns(next); setWire(r.messages);
    save({ turns: next, wire: r.messages });
  };

  const send = async () => {
    const said = draft.trim();
    if (!said || busy || pending) return;
    setDraft(""); setError(null); setBusy(true);

    const shown: Turn[] = [...turns, { who: "you", text: said }];
    setTurns(shown);

    try {
      settle(shown, await api.assistantChat({
        messages: [...wire, { role: "user", content: said }],
        ...(model ? { model } : {}),
      }));
    } catch (e: any) {
      // The turn that failed is dropped rather than left looking sent. Plain,
      // no apology, no advice.
      setTurns(turns);
      setDraft(said);
      setError(e?.message ?? "that did not go through");
    } finally {
      setBusy(false);
    }
  };

  /** Yes or no to the card. Only the yes writes anything. */
  const answer = async (yes: boolean) => {
    if (!pending || busy) return;
    setError(null); setBusy(true);
    const at = turns.length - 1;   // the open card is always the last thing shown
    const shown = [...turns];
    if (!yes && shown[at]?.who === "card") {
      shown[at] = { ...(shown[at] as Extract<Turn, { who: "card" }>), state: "cancelled" };
      setTurns(shown);
    }
    try {
      const body = { proposal_id: pending.id, messages: pending.wire, ...(model ? { model } : {}) };
      const r = yes ? await api.assistantConfirm(body) : await api.assistantDecline(body);
      settle(shown, r, yes ? at : undefined);
    } catch (e: any) {
      setTurns(turns);
      setError(e?.message ?? "that did not go through");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Put it back. One call, no model, no cost.
   *
   * The handle lives in the api container's memory, so it does not survive a
   * restart — the server says so plainly when it has gone, and that message is
   * shown rather than translated into something reassuring.
   */
  const undo = async (i: number) => {
    const card = turns[i];
    if (card?.who !== "card" || !card.batch || busy) return;
    setError(null); setBusy(true);
    try {
      await api.assistantUndo(card.batch);
      const next = [...turns];
      next[i] = { ...card, undone: true, batch: undefined };
      setTurns(next); save({ turns: next, wire });
    } catch (e: any) {
      setError(e?.message ?? "that did not go through");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setTurns([]); setWire([]); setLastSpend(null); setError(null); setPending(null);
    save({ turns: [], wire: [] });
  };

  const chosen = models.find((m) => m.id === model);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(30,31,36,0.35)",
        display: "flex", alignItems: "flex-end", zIndex: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.ground, width: "100%", maxWidth: 560, margin: "0 auto",
          borderRadius: "16px 16px 0 0", borderTop: `1px solid ${C.rule}`,
          display: "flex", flexDirection: "column", maxHeight: "min(88dvh, calc(100dvh - 24px - env(safe-area-inset-top)))",
        }}
      >
        {/* header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          padding: "13px 18px 9px", borderBottom: `1px solid ${C.rule}`,
        }}>
          <button
            onClick={() => setPicking((x) => !x)}
            style={{
              fontFamily: mono, fontSize: 11, letterSpacing: 1, color: C.inkFaint,
              background: "none", border: "none", padding: 0, cursor: "pointer",
              borderBottom: `1px dotted ${C.ruleStrong}`,
            }}
          >{(chosen?.label ?? "ASSISTANT").toUpperCase()}</button>
          <div style={{ display: "flex", gap: 14 }}>
            {turns.length > 0 && (
              <button onClick={clear} style={{
                fontFamily: mono, fontSize: 11, color: C.inkGhost,
                background: "none", border: "none", padding: 0, cursor: "pointer",
              }}>new</button>
            )}
            <button onClick={onClose} style={{
              fontFamily: mono, fontSize: 11, color: C.inkGhost,
              background: "none", border: "none", padding: 0, cursor: "pointer",
            }}>close</button>
          </div>
        </div>

        {/* The three, and nothing more expensive is ever in this list. It comes
            from the server, so it cannot grow by editing the app. */}
        {picking && (
          <div style={{ padding: "10px 18px", borderBottom: `1px solid ${C.rule}` }}>
            {models.map((m) => (
              <button
                key={m.id}
                onClick={async () => {
                  setModel(m.id); setPicking(false);
                  try { await api.updateSettings({ model: m.id }); } catch { /* this sitting only */ }
                }}
                style={{
                  display: "block", width: "100%", textAlign: "left", background: "none",
                  border: "none", padding: "8px 0", cursor: "pointer", fontFamily: sans,
                }}
              >
                <span style={{ fontSize: 14, color: C.ink, fontWeight: m.id === model ? 600 : 400 }}>{m.label}</span>
                <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginLeft: 10 }}>{m.note}</span>
              </button>
            ))}
          </div>
        )}

        {/* transcript */}
        <div style={{ overflowY: "auto", padding: "4px 18px 0", flex: 1 }}>
          {turns.length === 0 && (
            <p style={{ color: C.inkFaint, fontSize: 13, lineHeight: 1.5, margin: "14px 0" }}>
              It can see the whole board, the pantry and the documents on the server, and it can
              change the board. Every change is shown before it happens, and can be put back after.
            </p>
          )}

          {turns.map((t, i) => (
            <div key={i} style={{ margin: "14px 0" }}>
              <div style={{
                fontFamily: mono, fontSize: 10.5, letterSpacing: 1,
                color: C.inkGhost, marginBottom: 4,
              }}>{t.who === "you" ? "YOU" : t.who === "card" ? "THIS WOULD CHANGE THE BOARD" : "···"}</div>

              {t.who === "you" && (
                <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0, color: C.inkSoft }}>{t.text}</p>
              )}
              {t.who === "it" && <Markdown text={t.text} />}

              {t.who === "card" && (<>
                <Card previews={t.previews} />

                {/* Open: the two buttons, and nothing has been written. */}
                {t.state === "open" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                    <button
                      onClick={() => answer(true)}
                      disabled={busy}
                      style={{
                        padding: "9px 16px", borderRadius: 10, border: "none",
                        background: C.ink, color: C.ground, fontFamily: sans, fontSize: 14,
                        cursor: "pointer", opacity: busy ? 0.35 : 1,
                      }}
                    >Do it</button>
                    <button
                      onClick={() => answer(false)}
                      disabled={busy}
                      style={{
                        padding: "9px 16px", borderRadius: 10, fontFamily: sans, fontSize: 14,
                        border: `1px solid ${C.ruleStrong}`, background: "none", color: C.inkSoft,
                        cursor: "pointer", opacity: busy ? 0.35 : 1,
                      }}
                    >No</button>
                  </div>
                )}

                {/* Answered. Flat statements — no tick, no "great", nothing
                    congratulating him for having made a decision. */}
                {t.state === "cancelled" && (
                  <div style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost }}>not done</div>
                )}
                {t.state === "done" && (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                    <span style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost }}>
                      {t.undone ? "done, then put back" : "done"}
                    </span>
                    {t.batch && !t.undone && (
                      <button
                        onClick={() => undo(i)}
                        disabled={busy}
                        style={{
                          fontFamily: mono, fontSize: 11, color: C.inkFaint, background: "none",
                          border: "none", padding: 0, cursor: "pointer",
                          borderBottom: `1px dotted ${C.ruleStrong}`, opacity: busy ? 0.35 : 1,
                        }}
                      >undo</button>
                    )}
                  </div>
                )}
              </>)}
            </div>
          ))}

          {busy && <p style={{ color: C.inkFaint, fontSize: 13, margin: "14px 0" }}>Reading the board…</p>}
          {error && <p style={{ color: C.alert, fontSize: 13, margin: "14px 0" }}>{error}</p>}
          <div ref={foot} />
        </div>

        {/* What the last answer cost. Money, which is the one thing here
            allowed to carry a figure — and it is about the machine, not him. */}
        {lastSpend && !busy && (
          <div style={{
            fontFamily: mono, fontSize: 10.5, color: C.inkGhost,
            padding: "0 18px 6px", textAlign: "right",
          }}>{money(lastSpend.usd)}</div>
        )}

        <div style={{
          display: "flex", gap: 8, alignItems: "flex-end",
          padding: "10px 18px calc(14px + env(safe-area-inset-bottom))",
          borderTop: `1px solid ${C.rule}`,
        }}>
          {/* While a card is open the conversation waits on it. Typing past a
              pending change and having it answered later is how a board ends
              up in a state nobody chose. */}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            rows={1}
            disabled={!!pending}
            placeholder={pending ? "answer the change above first" : "what's blocking citizenship?"}
            style={{
              flex: 1, resize: "none", fontFamily: sans, fontSize: 16, lineHeight: 1.4,
              padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.ruleStrong}`,
              background: C.card, maxHeight: 120, boxSizing: "border-box",
              opacity: pending ? 0.5 : 1,
            }}
          />
          <button
            onClick={send}
            disabled={busy || !draft.trim() || !!pending}
            style={{
              padding: "11px 16px", borderRadius: 10, border: "none",
              background: C.ink, color: C.ground, fontFamily: sans, fontSize: 14,
              cursor: "pointer", opacity: draft.trim() && !busy && !pending ? 1 : 0.35,
            }}
          >{busy ? "…" : "Ask"}</button>
        </div>
      </div>
    </div>
  );
}
